import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Shield, ShieldOff, Unlock, Lock, RotateCcw, ChevronLeft, ChevronRight, Plus, UserPlus, Trash2, Target, Bell, BellRing, Smartphone, BookOpen } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useChallenge } from '@/contexts/ChallengeContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { userChallengeService } from '@/services/userChallengeService';
import { usePermissions } from '@/hooks/usePermissions';
import { 
  checkNotificationPermission, 
  requestLocalNotificationPermission, 
  showNotification,
  isNativePlatform 
} from '@/services/localNotificationService';
import { getPlatform, isPushSupported } from '@/services/pushNotificationService';
import { toDateOnly } from '@/lib/dateUtils';

interface DevToolbarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DevToolbar = ({ open, onOpenChange }: DevToolbarProps) => {
  const [skipAuth, setSkipAuth] = useState(false);
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const { progress, enrollmentId, refreshProgress, currentChallengeId } = useChallenge();
  const [isAdminMode, setIsAdminMode] = useState(user?.userType === 'admin');
  const [isViewingAsUser, setIsViewingAsUser] = useState(localStorage.getItem('sy_dev_force_non_admin') === 'true');
  const [areFeaturesUnlocked, setAreFeaturesUnlocked] = useState(false);
  const [isCoursesUnlocked, setIsCoursesUnlocked] = useState(localStorage.getItem('sy_dev_courses_unlocked') === 'true');
  
  // Challenge state
  const [challengeInfo, setChallengeInfo] = useState<{
    isEnrolled: boolean;
    currentDay: number;
    totalDays: number;
    preparationDays: number;
    challengeTitle: string;
    availableChallengesCount: number;
  } | null>(null);
  const [isLoadingChallenge, setIsLoadingChallenge] = useState(false);

  // Notification state
  const [notificationStatus, setNotificationStatus] = useState<{
    permission: 'granted' | 'denied' | 'default' | 'unknown';
    platform: 'web' | 'android' | 'ios';
    deviceRegistered: boolean;
    pushSupported: boolean;
  }>({ permission: 'unknown', platform: 'web', deviceRegistered: false, pushSupported: false });
  const [isTestingNotification, setIsTestingNotification] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem('sy_dev_skip_auth') === 'true';
    setSkipAuth(stored);
    
    const devForceAdmin = localStorage.getItem('sy_dev_force_admin') === 'true';

    if (stored) {
      setIsAdminMode(devForceAdmin);
    } else {
      const encryptedData = localStorage.getItem('sy_user_data');
      if (encryptedData) {
        try {
          const currentUser = JSON.parse(atob(encryptedData));
          setIsAdminMode(currentUser?.userType === 'admin');
        } catch (e) {
          setIsAdminMode(false);
        }
      } else {
        setIsAdminMode(false);
      }
    }
  }, []);

  useEffect(() => {
    const checkFeaturesStatus = async () => {
      if (!user?.id) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('unlocked_features')
        .eq('id', user.id)
        .single();

      if (profile?.unlocked_features && typeof profile.unlocked_features === 'object') {
        const features = profile.unlocked_features as any;
        const areUnlocked = features.chat_with_tobi && features.community_feed;
        setAreFeaturesUnlocked(areUnlocked);
      }
    };

    checkFeaturesStatus();
  }, [user?.id]);

  // Load challenge info
  useEffect(() => {
    const loadChallengeInfo = async () => {
      if (!user?.id) {
        setChallengeInfo(null);
        return;
      }

      try {
        const activeChallenge = await userChallengeService.getUserActiveChallenge(user.id);
        
        const { data: availableChallenges } = await supabase
          .from('challenges')
          .select('id')
          .eq('challenge_type', 'entry')
          .or(`status.eq.active,status.eq.upcoming`);

        if (activeChallenge) {
          const { data: challenge } = await supabase
            .from('challenges')
            .select('title, total_days, preparation_days')
            .eq('id', activeChallenge.challenge_id)
            .single();

          setChallengeInfo({
            isEnrolled: true,
            currentDay: activeChallenge.current_day,
            totalDays: challenge?.total_days || 14,
            preparationDays: challenge?.preparation_days || 0,
            challengeTitle: challenge?.title || 'אתגר',
            availableChallengesCount: availableChallenges?.length || 0
          });
        } else {
          setChallengeInfo({
            isEnrolled: false,
            currentDay: 0,
            totalDays: 0,
            preparationDays: 0,
            challengeTitle: '',
            availableChallengesCount: availableChallenges?.length || 0
          });
        }
      } catch (e) {
        console.error('[DevToolbar] Error loading challenge info:', e);
      }
    };

    loadChallengeInfo();
  }, [user?.id, enrollmentId]);

  // Load notification status
  useEffect(() => {
    const loadNotificationStatus = async () => {
      try {
        const permission = await checkNotificationPermission();
        const platform = getPlatform();
        const pushSupported = isPushSupported();
        
        let deviceRegistered = false;
        if (user?.id) {
          const { data: devices } = await supabase
            .from('user_devices')
            .select('id')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .limit(1);
          deviceRegistered = (devices?.length || 0) > 0;
        }

        setNotificationStatus({
          permission: permission ? 'granted' : 'default',
          platform,
          deviceRegistered,
          pushSupported
        });
      } catch (e) {
        console.error('[DevToolbar] Error loading notification status:', e);
      }
    };

    loadNotificationStatus();
  }, [user?.id]);

  // SECURITY: רק אדמינים יכולים לראות את כלי הפיתוח
  if (!isAdmin()) {
    return null;
  }

  // If not open, render nothing (trigger is in Header)
  if (!open) {
    return null;
  }

  const toggleSkipAuth = () => {
    const newValue = !skipAuth;
    setSkipAuth(newValue);
    localStorage.setItem('sy_dev_skip_auth', newValue.toString());
    window.location.reload();
  };

  const toggleAdminMode = () => {
    const newValue = !isAdminMode;
    localStorage.setItem('sy_dev_force_admin', newValue.toString());
    setIsAdminMode(newValue);
    toast.success(newValue ? 'מצב אדמין הופעל (פיתוח מקומי)' : 'מצב אדמין בוטל (פיתוח מקומי)');
    setTimeout(() => window.location.reload(), 500);
  };

  const unlockAllFeatures = async () => {
    if (!user?.id) {
      toast.error('יש להתחבר תחילה');
      return;
    }

    try {
      const newState = !areFeaturesUnlocked;
      const featureFlags = {
        chat_with_tobi: newState,
        water_tracking: newState,
        fasting_tracking: newState,
        steps_tracking: newState,
        community_feed: newState
      };

      // Merge with existing data to preserve non-feature keys (e.g. custom_onboarding_fields)
      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('unlocked_features')
        .eq('id', user.id)
        .single();

      const existing = (currentProfile?.unlocked_features as Record<string, unknown>) || {};
      const merged = { ...existing, ...featureFlags };

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ unlocked_features: merged })
        .eq('id', user.id);

      if (updateError) throw updateError;
      
      toast.success(areFeaturesUnlocked ? 'כל הפיצרים ננעלו!' : 'כל הפיצרים נפתחו באופן גלובלי!');
      
      if (refreshProgress) await refreshProgress();
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      console.error('Error toggling features:', e);
      toast.error('שגיאה בשינוי מצב הפיצרים');
    }
  };

  const toggleCoursesLock = () => {
    const newValue = !isCoursesUnlocked;
    localStorage.setItem('sy_dev_courses_unlocked', newValue.toString());
    setIsCoursesUnlocked(newValue);
    toast.success(newValue ? 'טאב קורסים נפתח לך!' : 'טאב קורסים ננעל');
    setTimeout(() => window.location.reload(), 500);
  };

  const resetOnboarding = async () => {
    if (!user?.id) {
      toast.error('יש להתחבר תחילה');
      return;
    }

    try {
      const { error: answersError } = await supabase
        .from('user_onboarding_answers')
        .delete()
        .eq('user_id', user.id);

      if (answersError) {
        console.error('[DevToolbar] Error deleting answers:', answersError);
      }

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          has_completed_onboarding: false,
          current_onboarding_step_id: null,
          display_name: null,
          age: null,
          location: null,
          about_me: null,
          hobbies: null,
          pronouns: null,
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      toast.success('אונבורדינג אופס בהצלחה!');
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      console.error('[DevToolbar] Error resetting onboarding:', e);
      toast.error('שגיאה באיפוס אונבורדינג');
    }
  };

  const changeDay = async (delta: number) => {
    if (!enrollmentId || !challengeInfo?.isEnrolled) {
      toast.error('אין רישום פעיל לאתגר');
      return;
    }

    const minDay = challengeInfo.preparationDays > 0 ? -challengeInfo.preparationDays : 1;
    const maxDay = challengeInfo.totalDays;
    
    let newDay = challengeInfo.currentDay + delta;
    
    if (newDay === 0) {
      newDay = delta > 0 ? 1 : -1;
    }
    
    if (newDay < minDay || newDay > maxDay) {
      toast.error(`יום חייב להיות בין ${minDay} ל-${maxDay}`);
      return;
    }

    setIsLoadingChallenge(true);
    try {
      await userChallengeService.updateCurrentDay(enrollmentId, newDay);
      setChallengeInfo(prev => prev ? { ...prev, currentDay: newDay } : null);
      toast.success(`עברת ליום ${newDay}`);
      if (refreshProgress) await refreshProgress();
    } catch (e) {
      console.error('[DevToolbar] Error changing day:', e);
      toast.error('שגיאה בשינוי יום');
    } finally {
      setIsLoadingChallenge(false);
    }
  };

  const createTestChallenge = async () => {
    setIsLoadingChallenge(true);
    try {
      const today = new Date();
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 13);

      const { data: challenge, error: challengeError } = await supabase
        .from('challenges')
        .insert({
          title: `אתגר בדיקה ${today.toLocaleDateString('he-IL')}`,
          description: 'אתגר שנוצר לצורך בדיקות פיתוח',
          challenge_type: 'entry',
          status: 'active',
          start_date: toDateOnly(today),
          end_date: toDateOnly(endDate),
          total_days: 14
        })
        .select()
        .single();

      if (challengeError) throw challengeError;

      const daysToCreate = [];
      for (let i = 1; i <= 14; i++) {
        daysToCreate.push({
          challenge_id: challenge.id,
          day_number: i,
          video_id: `test-video-${i}`,
          video_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
          video_title: `יום ${i} - סרטון בדיקה`,
          video_description: `תיאור סרטון ליום ${i}`
        });
      }

      const { error: daysError } = await supabase
        .from('challenge_days')
        .insert(daysToCreate);

      if (daysError) throw daysError;

      toast.success('אתגר בדיקה נוצר בהצלחה!');
      setChallengeInfo(prev => prev ? { ...prev, availableChallengesCount: (prev.availableChallengesCount || 0) + 1 } : null);
    } catch (e) {
      console.error('[DevToolbar] Error creating test challenge:', e);
      toast.error('שגיאה ביצירת אתגר בדיקה');
    } finally {
      setIsLoadingChallenge(false);
    }
  };

  const enrollInChallenge = async () => {
    if (!user?.id) {
      toast.error('יש להתחבר תחילה');
      return;
    }

    setIsLoadingChallenge(true);
    try {
      const { data: availableChallenge } = await supabase
        .from('challenges')
        .select('id, title')
        .eq('challenge_type', 'entry')
        .or('status.eq.active,status.eq.upcoming')
        .order('start_date', { ascending: true })
        .limit(1)
        .single();

      if (!availableChallenge) {
        toast.error('אין אתגרי כניסה זמינים - צור אתגר בדיקה קודם');
        return;
      }

      await userChallengeService.enrollInChallenge(availableChallenge.id);
      toast.success(`נרשמת לאתגר: ${availableChallenge.title}`);
      
      if (refreshProgress) await refreshProgress();
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      console.error('[DevToolbar] Error enrolling in challenge:', e);
      toast.error('שגיאה ברישום לאתגר');
    } finally {
      setIsLoadingChallenge(false);
    }
  };

  const resetChallengeEnrollment = async () => {
    if (!user?.id) {
      toast.error('יש להתחבר תחילה');
      return;
    }

    setIsLoadingChallenge(true);
    try {
      const { data: enrollments } = await supabase
        .from('user_challenge_enrollments')
        .select('id')
        .eq('user_id', user.id);

      const enrollmentIds = enrollments?.map(e => e.id) || [];

      if (enrollmentIds.length > 0) {
        await supabase
          .from('user_challenge_progress')
          .delete()
          .in('enrollment_id', enrollmentIds);
      }

      const { error: enrollmentError } = await supabase
        .from('user_challenge_enrollments')
        .delete()
        .eq('user_id', user.id);

      if (enrollmentError) throw enrollmentError;

      toast.success('רישום לאתגר אופס בהצלחה!');
      setChallengeInfo(prev => prev ? { ...prev, isEnrolled: false, currentDay: 0, totalDays: 0, preparationDays: 0, challengeTitle: '' } : null);
      
      if (refreshProgress) await refreshProgress();
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      console.error('[DevToolbar] Error resetting enrollment:', e);
      toast.error('שגיאה באיפוס רישום');
    } finally {
      setIsLoadingChallenge(false);
    }
  };

  const testLocalNotification = async () => {
    setIsTestingNotification(true);
    try {
      const result = await showNotification('בדיקת התראה מקומית', 'ההתראות עובדות! 🎉');
      if (result) {
        toast.success('התראה מקומית נשלחה!');
      } else {
        toast.error('שליחת התראה נכשלה - בדוק הרשאות');
      }
    } catch (e) {
      console.error('[DevToolbar] Error testing local notification:', e);
      toast.error('שגיאה בשליחת התראה מקומית');
    } finally {
      setIsTestingNotification(false);
    }
  };

  const testPushNotification = async () => {
    if (!user?.id) {
      toast.error('יש להתחבר תחילה');
      return;
    }

    setIsTestingNotification(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: { 
          user_ids: [user.id], 
          title: 'בדיקת Push Notification', 
          body: 'Push Notification עובדת! 🚀' 
        }
      });

      if (error) throw error;
      
      toast.success(`Push נשלח! (${data?.push_sent || 0} מכשירים)`);
    } catch (e) {
      console.error('[DevToolbar] Error testing push notification:', e);
      toast.error('שגיאה בשליחת Push Notification');
    } finally {
      setIsTestingNotification(false);
    }
  };

  const requestPermission = async () => {
    setIsTestingNotification(true);
    try {
      const result = await requestLocalNotificationPermission();
      if (result) {
        toast.success('הרשאות התראות אושרו!');
        setNotificationStatus(prev => ({ ...prev, permission: 'granted' }));
      } else {
        toast.error('הרשאות נדחו');
        setNotificationStatus(prev => ({ ...prev, permission: 'denied' }));
      }
    } catch (e) {
      console.error('[DevToolbar] Error requesting permission:', e);
      toast.error('שגיאה בבקשת הרשאות');
    } finally {
      setIsTestingNotification(false);
    }
  };

  return (
    <>
      {/* Backdrop — closes panel on outside click */}
      <div
        className="fixed inset-0 z-[9998]"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />
      <div className="fixed top-16 left-4 z-[9999]">
        <div 
          className="bg-card rounded-lg shadow-xl border p-4 min-w-[280px] max-h-[80vh] overflow-y-auto" 
          dir="rtl"
        >
        <div className="text-sm font-semibold text-card-foreground mb-3">כלי פיתוח</div>
        
        <div className="space-y-2">
          <button
            onClick={toggleSkipAuth}
            className={`flex items-center gap-2 w-full p-2 rounded text-sm transition-colors ${
              skipAuth 
                ? 'bg-primary/10 text-primary hover:bg-primary/20' 
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {skipAuth ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            {skipAuth ? 'אימות מופעל' : 'דלג על אימות'}
          </button>
          
          <button
            onClick={toggleAdminMode}
            className={`flex items-center gap-2 w-full p-2 rounded text-sm transition-colors ${
              isAdminMode 
                ? 'bg-primary/10 text-primary hover:bg-primary/20' 
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {isAdminMode ? <Shield className="h-4 w-4" /> : <ShieldOff className="h-4 w-4" />}
            {isAdminMode ? 'מצב אדמין פעיל' : 'הפעל מצב אדמין'}
          </button>

          {(user?.userType === 'admin' || isAdminMode) && (
            <button
              onClick={() => {
                const newValue = !isViewingAsUser;
                localStorage.setItem('sy_dev_force_non_admin', newValue.toString());
                setIsViewingAsUser(newValue);
                toast.success(newValue ? 'צופה כמשתמש רגיל' : 'חזרה למצב אדמין');
                setTimeout(() => window.location.reload(), 500);
              }}
              className={`flex items-center gap-2 w-full p-2 rounded text-sm transition-colors ${
                isViewingAsUser
                  ? 'bg-purple-500/10 text-purple-600 hover:bg-purple-500/20'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Eye className="h-4 w-4" />
              {isViewingAsUser ? 'צופה כמשתמש רגיל' : 'צפה כמשתמש רגיל'}
            </button>
          )}

          <button
            onClick={unlockAllFeatures}
            disabled={!user?.id}
            className={`flex items-center gap-2 w-full p-2 rounded text-sm transition-colors ${
              user?.id
                ? areFeaturesUnlocked
                  ? 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
                  : 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
                : 'bg-muted text-muted-foreground/50 cursor-not-allowed'
            }`}
          >
            <Unlock className="h-4 w-4" />
            {areFeaturesUnlocked ? 'נעל את כל הפיצרים' : 'פתח את כל הפיצרים'}
          </button>

          <button
            onClick={resetOnboarding}
            disabled={!user?.id}
            className={`flex items-center gap-2 w-full p-2 rounded text-sm transition-colors ${
              user?.id
                ? 'bg-orange-500/10 text-orange-600 hover:bg-orange-500/20'
                : 'bg-muted text-muted-foreground/50 cursor-not-allowed'
            }`}
          >
            <RotateCcw className="h-4 w-4" />
            אפס אונבורדינג
          </button>
        </div>

        {/* Courses Lock Section */}
        <div className="mt-4 pt-3 border-t">
          <div className="flex items-center gap-2 text-sm font-semibold text-card-foreground mb-3">
            <BookOpen className="h-4 w-4" />
            קורסים
          </div>
          <button
            onClick={toggleCoursesLock}
            className={`flex items-center gap-2 w-full p-2 rounded text-sm transition-colors ${
              isCoursesUnlocked
                ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
                : 'bg-yellow-500/10 text-yellow-700 hover:bg-yellow-500/20'
            }`}
          >
            {isCoursesUnlocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            {isCoursesUnlocked ? 'טאב קורסים פתוח (לחץ לנעול)' : 'פתח טאב קורסים לעצמך'}
          </button>
        </div>

        {/* Challenge Section */}
        <div className="mt-4 pt-3 border-t">
          <div className="flex items-center gap-2 text-sm font-semibold text-card-foreground mb-3">
            <Target className="h-4 w-4" />
            אתגרים
          </div>

          <div className="bg-muted/50 rounded-lg p-3 mb-3 text-sm">
            {challengeInfo?.isEnrolled ? (
              <>
                <div className="text-muted-foreground mb-1">רשום ל: <span className="text-foreground font-medium">{challengeInfo.challengeTitle}</span></div>
                
                <div className="flex items-center justify-center gap-3 my-2">
                  <button
                    onClick={() => changeDay(-1)}
                    disabled={isLoadingChallenge || challengeInfo.currentDay <= (challengeInfo.preparationDays > 0 ? -challengeInfo.preparationDays : 1)}
                    className="p-1 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                  <span 
                    className={`font-bold text-lg min-w-[100px] text-center ${challengeInfo.currentDay < 0 ? 'text-amber-600' : ''}`}
                    dir="ltr"
                  >
                    {`${challengeInfo.currentDay} / ${challengeInfo.totalDays}`}
                  </span>
                  <button
                    onClick={() => changeDay(1)}
                    disabled={isLoadingChallenge || challengeInfo.currentDay >= challengeInfo.totalDays}
                    className="p-1 rounded hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                </div>
                {challengeInfo.preparationDays > 0 && (
                  <div className="text-xs text-amber-600 text-center mt-1">
                    כולל {challengeInfo.preparationDays} ימי הכנה (ימים -{challengeInfo.preparationDays} עד -1)
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted-foreground text-center">לא רשום לאתגר</div>
            )}
            <div className="text-xs text-muted-foreground mt-2 text-center">
              {challengeInfo?.availableChallengesCount || 0} אתגרי כניסה זמינים
            </div>
          </div>

          <div className="space-y-2">
            <button
              onClick={createTestChallenge}
              disabled={isLoadingChallenge}
              className="flex items-center gap-2 w-full p-2 rounded text-sm bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              צור אתגר בדיקה (14 ימים)
            </button>

            <button
              onClick={enrollInChallenge}
              disabled={isLoadingChallenge || !user?.id || challengeInfo?.isEnrolled}
              className={`flex items-center gap-2 w-full p-2 rounded text-sm transition-colors ${
                challengeInfo?.isEnrolled
                  ? 'bg-muted text-muted-foreground/50 cursor-not-allowed'
                  : 'bg-green-500/10 text-green-600 hover:bg-green-500/20'
              } disabled:opacity-50`}
            >
              <UserPlus className="h-4 w-4" />
              {challengeInfo?.isEnrolled ? 'כבר רשום לאתגר' : 'רשום לאתגר כניסה'}
            </button>

            <button
              onClick={resetChallengeEnrollment}
              disabled={isLoadingChallenge || !user?.id || !challengeInfo?.isEnrolled}
              className={`flex items-center gap-2 w-full p-2 rounded text-sm transition-colors ${
                !challengeInfo?.isEnrolled
                  ? 'bg-muted text-muted-foreground/50 cursor-not-allowed'
                  : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
              } disabled:opacity-50`}
            >
              <Trash2 className="h-4 w-4" />
              אפס רישום לאתגר
            </button>
          </div>
        </div>

        {/* Notifications Section */}
        <div className="mt-4 pt-3 border-t">
          <div className="flex items-center gap-2 text-sm font-semibold text-card-foreground mb-3">
            <Bell className="h-4 w-4" />
            בדיקת התראות
          </div>

          <div className="bg-muted/50 rounded-lg p-3 mb-3 text-xs space-y-1">
            <div className="flex items-center gap-2">
              <Smartphone className="h-3 w-3" />
              <span className="text-muted-foreground">פלטפורמה:</span>
              <span className="font-medium">{notificationStatus.platform}</span>
              {isNativePlatform() && <span className="text-green-600">(Native)</span>}
            </div>
            <div>
              <span className="text-muted-foreground">הרשאה:</span>{' '}
              <span className={`font-medium ${notificationStatus.permission === 'granted' ? 'text-green-600' : 'text-amber-600'}`}>
                {notificationStatus.permission === 'granted' ? 'אושרה ✓' : notificationStatus.permission === 'denied' ? 'נדחתה ✗' : 'לא נשאל'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Push נתמך:</span>{' '}
              <span className={`font-medium ${notificationStatus.pushSupported ? 'text-green-600' : 'text-muted-foreground'}`}>
                {notificationStatus.pushSupported ? 'כן ✓' : 'לא'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">מכשיר רשום:</span>{' '}
              <span className={`font-medium ${notificationStatus.deviceRegistered ? 'text-green-600' : 'text-amber-600'}`}>
                {notificationStatus.deviceRegistered ? 'כן ✓' : 'לא'}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            {notificationStatus.permission !== 'granted' && (
              <button
                onClick={requestPermission}
                disabled={isTestingNotification}
                className="flex items-center gap-2 w-full p-2 rounded text-sm bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 transition-colors disabled:opacity-50"
              >
                <Bell className="h-4 w-4" />
                בקש הרשאות התראות
              </button>
            )}

            <button
              onClick={testLocalNotification}
              disabled={isTestingNotification}
              className="flex items-center gap-2 w-full p-2 rounded text-sm bg-blue-500/10 text-blue-600 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
            >
              <BellRing className="h-4 w-4" />
              בדוק התראה מקומית
            </button>

            <button
              onClick={testPushNotification}
              disabled={isTestingNotification || !user?.id}
              className="flex items-center gap-2 w-full p-2 rounded text-sm bg-purple-500/10 text-purple-600 hover:bg-purple-500/20 transition-colors disabled:opacity-50"
            >
              <Smartphone className="h-4 w-4" />
              בדוק Push Notification
            </button>
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-t text-xs text-muted-foreground/60">
          כלי פיתוח - אדמינים בלבד
        </div>
      </div>
    </div>
    </>
  );
};

export default DevToolbar;
